// src/utils/eventRosterPdf.js
// Per-event printable SIGN-IN SHEET — imperative jsPDF (no html2canvas /
// doc.html(), per project convention). Includes the event's QR (scan to join +
// check in), the confirmed roster with signature + time-in lines, and blank
// rows for walk-ups. ASCII-only glyphs so Helvetica renders cleanly.

import jsPDF from "jspdf";

const NAVY = [35, 60, 95];
const GOLD = [162, 145, 100];
const RED = [232, 53, 48];

function fmt(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-US", {
      timeZone: "Asia/Amman",
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
}

// qrDataUrl: a PNG data URL for the event's join link (generated by the caller).
export function downloadEventSignInSheet(event, signups, qrDataUrl) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Header band
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 72, "F");
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("KING'S ACADEMY - TRIPS & EVENTS", margin, 30);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text(event.name || "Event", margin, 54);

  // QR top-right with caption
  const qrSize = 120;
  const qrX = pageW - margin - qrSize;
  const qrY = 88;
  if (qrDataUrl) {
    try { doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize); } catch { /* ignore */ }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...NAVY);
    doc.text("SCAN TO JOIN & CHECK IN", qrX, qrY + qrSize + 12);
  }

  // Meta (left column)
  let y = 100;
  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const meta = [
    `Start:  ${fmt(event.startAt)}`,
    `End:    ${fmt(event.endAt)}`,
    `Chaperones: ${(event.chaperones || []).map((c) => c.name || c.email).join(", ") || "none"}`,
    `Capacity: ${event.capacity ?? "none"} students     Students: ${event.studentCount ?? event.signedUpCount ?? 0}     Teachers: ${event.staffCount || 0}     Waitlist: ${event.waitlistCount || 0}`,
  ];
  meta.forEach((l) => { doc.text(l, margin, y); y += 16; });

  // Drop below the QR block before the table
  y = Math.max(y + 6, qrY + qrSize + 28);
  doc.setDrawColor(210); doc.line(margin, y, pageW - margin, y); y += 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...NAVY);
  doc.text("Sign-In Sheet", margin, y);
  y += 18;

  // Columns
  const numX = margin;
  const nameX = margin + 28;
  const sigX = margin + 240;
  const timeX = pageW - margin - 90;
  const rowH = 28;

  function drawHeader() {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...NAVY);
    doc.text("#", numX, y);
    doc.text("Name", nameX, y);
    doc.text("Signature", sigX, y);
    doc.text("Time In", timeX, y);
    y += 6; doc.setDrawColor(190); doc.line(margin, y, pageW - margin, y); y += rowH - 6;
    doc.setFont("helvetica", "normal");
  }
  drawHeader();

  // Pre-fill confirmed names (teachers labeled); pad with blank rows for walk-ups.
  const confirmed = (signups || [])
    .filter((s) => s.status === "confirmed")
    .map((s) => ({ name: s.name || s.email || "", role: s.role || "student" }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const target = Math.max(20, (event.capacity || confirmed.length) + 6);
  const totalRows = Math.max(confirmed.length, target);

  for (let i = 0; i < totalRows; i++) {
    if (y > pageH - margin - rowH) { doc.addPage(); y = margin; drawHeader(); }
    const entry = i < confirmed.length ? confirmed[i] : null;
    const label = entry ? entry.name + (entry.role === "staff" ? "  (Teacher)" : "") : "";
    doc.setFontSize(10); doc.setTextColor(40, 40, 40);
    doc.text(String(i + 1), numX, y);
    if (label) doc.text(label.slice(0, 42), nameX, y);

    // Writing lines
    doc.setDrawColor(205);
    if (!label) doc.line(nameX, y + 4, sigX - 12, y + 4); // name line for walk-ups
    doc.line(sigX, y + 4, timeX - 12, y + 4);             // signature line
    doc.line(timeX, y + 4, pageW - margin, y + 4);         // time-in line
    y += rowH;
  }

  const safe = (event.name || "event").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
  doc.save(`signin-${safe || "event"}.pdf`);
}

// ===========================================================================
// downloadEventBoardingCard — a SEPARATE one-page boarding card, kept distinct
// from the check-in sheet so the two QRs can't be confused. Big centered
// boarding QR (red accent), scanned by each student as they step onto the bus.
// qrDataUrl must encode the boarding link (…/trip-join?s=<token>&c=board).
// ===========================================================================
export function downloadEventBoardingCard(event, qrDataUrl) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Red header band to visually separate it from the navy check-in sheet.
  doc.setFillColor(...RED);
  doc.rect(0, 0, pageW, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("KING'S ACADEMY - BUS BOARDING", margin, 34);
  doc.setFontSize(20);
  doc.text(event.name || "Event", margin, 62);

  // Big centered QR.
  const qrSize = 300;
  const qrX = (pageW - qrSize) / 2;
  const qrY = 150;
  if (qrDataUrl) {
    try { doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize); } catch { /* ignore */ }
  }

  // Big instruction under the QR.
  doc.setTextColor(...RED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("SCAN AS YOU BOARD", pageW / 2, qrY + qrSize + 48, { align: "center" });

  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.text(
    "Every student scans this to confirm they are on the bus.",
    pageW / 2,
    qrY + qrSize + 74,
    { align: "center" }
  );

  doc.setTextColor(120, 120, 120);
  doc.setFontSize(11);
  doc.text(
    `Start: ${fmt(event.startAt)}`,
    pageW / 2,
    qrY + qrSize + 98,
    { align: "center" }
  );

  // Footer note distinguishing it from check-in.
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(
    "This is the BOARDING QR — different from the check-in sheet.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 40,
    { align: "center" }
  );

  const safe = (event.name || "event").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
  doc.save(`boarding-${safe || "event"}.pdf`);
}